import React, { useEffect, useState, useRef, EventHandler } from 'react';
import { makeCSS } from '../../../utils/makeCSS';
import { cx } from '@emotion/css';
import { keyframes } from '@emotion/react';
import { Typography } from '../Typography';

export type ReviewStatusType = 'pending' | 'approved' | 'rejected';
export type ContractStatusType =
  | 'active'
  | 'expired'
  | 'expires soon'
  | 'not started';
export type StatusType = ReviewStatusType | ContractStatusType;

const getColor = (status: StatusType) => {
  switch (status) {
    case 'active':
    case 'approved':
      return 'positive';
    case 'pending':
    case 'not started':
      return 'secondary';
    case 'expires soon':
      return 'warning';
    case 'expired':
    case 'rejected':
      return 'critical';
    default:
      return 'secondary';
  }
};

/*ReviewStatus is a visual indicator that represents the status of a review, whether it's approved, rejected or pending. */
export interface StatusProps {
  /**The status that the review has */
  status?: StatusType;
  /**Whether the animation is allowed or not*/
  //The animation will be used to indicate that the status of the review has change, but because the same ReviewStatus appears multiple times in the same page, we don't want all of them to have the animation because it will be too much.
  allowAnimation?: boolean;
  /**Makes sure that the animation gets retriggered.*/
  onAnimationEnd?: EventHandler<React.AnimationEvent>;
  /**Display the status text next to status */
  useText?: boolean;
  /** label displayed in the right side by default */
  reverse?: boolean;
}

const animation = keyframes({
  '0%': {
    opacity: 0.08,
    width: 24,
    height: 24
  },
  '40%': {
    opacity: 0.5,
    width: 12,
    height: 12
  },
  '50%': {
    opacity: 0.04,
    width: 24,
    height: 24
  },
  '80%': {
    opacity: 0.5,
    width: 12,
    height: 12
  },
  '100%': {
    opacity: 0.08,
    width: 24,
    height: 24
  }
});

const useCSS = makeCSS(({ theme }) => {
  return {
    root: {
      position: 'relative',
      display: 'flex',
      flexShrink: 0,
      alignItems: 'center',
      justifyContent: 'center',
      width: theme.spacing[6],
      height: theme.spacing[6]
    },
    container: {
      width: 'fit-containt',
      display: 'inline-flex',
      gap: 8,
      alignItems: 'center'
    },
    reverse: {
      flexDirection: 'row-reverse'
    },
    outsideDiv: {
      position: 'absolute',
      width: 'inherit',
      height: 'inherit',
      borderRadius: theme.borderRadius.full,
      opacity: 0.08,
      boxShadow: theme.color.background.primary
    },
    insideDiv: {
      position: 'absolute',
      opacity: 1,
      borderRadius: theme.borderRadius.full,
      width: theme.spacing[2],
      height: theme.spacing[2]
    },
    secondary: {
      background: theme.color.gray[30]
    },
    warning: {
      background: theme.color.orange[30]
    },
    positive: {
      background: theme.color.green[25]
    },
    critical: {
      background: theme.color.red[35]
    },
    reviewAnimation: {
      animation: `${animation} 1.2s ease-in`
    },
    label: {
      userSelect: 'none'
    }
  };
});

export const Status = (props: StatusProps) => {
  const {
    status = 'pending',
    allowAnimation = false,
    onAnimationEnd,
    useText = false,
    reverse = false
  } = props;

  const [shouldAnimate, setShouldAnimate] = useState(false);

  const classes = useCSS();

  const originalStatus = useRef(status);

  useEffect(() => {
    if (originalStatus.current !== status) {
      setShouldAnimate(true);
      originalStatus.current = status;
    }
  }, [status]);

  //makes sure that the animation will be retriggered
  const handleAnimation = (event) => {
    if (typeof onAnimationEnd === 'function') {
      onAnimationEnd(event);
    }
    setShouldAnimate(false);
  };

  const color = getColor(status);

  return (
    <div className={`${classes.container} ${reverse ? classes.reverse : ''}`}>
      <div onAnimationEnd={handleAnimation} className={`${classes.root}`}>
        <div
          className={cx(
            classes.outsideDiv,
            classes[color],
            allowAnimation ? (shouldAnimate ? classes.reviewAnimation : '') : ''
          )}
        ></div>
        <div className={`${classes.insideDiv} ${classes[color]} `}></div>
      </div>
      {useText && (
        <div className={`${classes.label}}`}>
          <Typography variant="md" colorVariant="03">
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </Typography>
        </div>
      )}
    </div>
  );
};
